/**
 * Financial Balance Page (formerly Home Page)
 * Dashboard with budget input, donut chart, and category breakdown
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useBudgetStore, type BudgetCategory } from '../store/budgetStore';
import { useUserPreferences, useUpdatePreferences } from '../hooks/useUserPreferences';
import { useUIStore } from '../store/uiStore';
import { useAuthStore } from '../store/authStore';
import { getCategoryName } from '../components/ui/CategoryLabel';

// Default fallback percentages matching the 6 budget categories
// Used when user has no saved configuration or percentages are invalid (sum to 0 or not 100)
const DEFAULT_FALLBACK_CATEGORIES: BudgetCategory[] = [
  { id: 'custo-fixo', name: 'Custo Fixo', nameEn: 'Fixed Costs', percentage: 30, color: '#f97316', icon: 'building' },
  { id: 'liberdade', name: 'Liberdade Financeira', nameEn: 'Financial Freedom', percentage: 25, color: '#a855f7', icon: 'dollar' },
  { id: 'conforto', name: 'Conforto', nameEn: 'Comfort', percentage: 15, color: '#3b82f6', icon: 'heart' },
  { id: 'metas', name: 'Metas', nameEn: 'Goals', percentage: 10, color: '#22c55e', icon: 'target' },
  { id: 'prazeres', name: 'Prazeres', nameEn: 'Pleasures', percentage: 15, color: '#eab308', icon: 'smile' },
  { id: 'conhecimento', name: 'Conhecimento', nameEn: 'Knowledge', percentage: 5, color: '#06b6d4', icon: 'book' },
];

/**
 * Check if categories have valid percentages (sum to 100 and all non-zero)
 */
function hasValidPercentages(categories: { percentage: number }[]): boolean {
  if (!categories || categories.length === 0) return false;
  const total = categories.reduce((sum, cat) => sum + cat.percentage, 0);
  return total === 100;
}

// Format number as Brazilian currency
const formatCurrency = (value: number): string => {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Parse Brazilian currency string to number
const parseCurrency = (value: string): number => {
  const cleaned = value.replace(/[^\d]/g, '');
  return parseFloat(cleaned) / 100 || 0;
};

export function FinancialBalancePage() {
  const { t } = useTranslation();
  const { totalBudget, setTotalBudget, categories: storeCategories } = useBudgetStore();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';
  const [inputValue, setInputValue] = useState(formatCurrency(totalBudget));
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch user preferences (syncs theme and budget with stores)
  const { preferences, isLoading: prefsLoading } = useUserPreferences();
  const updatePreferences = useUpdatePreferences();
  const { isAuthenticated } = useAuthStore();

  // Robust percentage logic: Use categories from user profile if valid, otherwise fallback to defaults
  // Priority: 1. Server preferences (if valid) 2. Store categories (if valid) 3. Default values
  const categories = useMemo(() => {
    // First, check if server preferences have valid percentages
    const serverCategories = preferences?.budgetConfig?.categories;
    if (serverCategories && hasValidPercentages(serverCategories)) {
      // Map server categories to BudgetCategory format
      const iconMap: Record<string, string> = {
        fixed_costs: 'building', financial_freedom: 'dollar', comfort: 'heart',
        goals: 'target', pleasures: 'smile', knowledge: 'book',
      };
      const nameEnMap: Record<string, string> = {
        fixed_costs: 'Fixed Costs', financial_freedom: 'Financial Freedom', comfort: 'Comfort',
        goals: 'Goals', pleasures: 'Pleasures', knowledge: 'Knowledge',
      };
      return serverCategories.map((cat: { id: string; nameKey: string; name?: string; color?: string; percentage: number }) => ({
        id: cat.nameKey || cat.id,
        name: cat.name || cat.nameKey,
        nameEn: nameEnMap[cat.nameKey] || cat.name || cat.nameKey,
        percentage: cat.percentage,
        color: cat.color || DEFAULT_FALLBACK_CATEGORIES.find(d => d.id === cat.nameKey)?.color || '#6b7280',
        icon: iconMap[cat.nameKey] || 'circle',
      }));
    }

    // Second, check if store categories have valid percentages
    if (storeCategories.length > 0 && hasValidPercentages(storeCategories)) {
      return storeCategories;
    }

    // Finally, use default fallback categories
    return DEFAULT_FALLBACK_CATEGORIES;
  }, [storeCategories, preferences]);

  // Prepare data for pie chart
  const chartData = categories.map((cat) => ({
    name: cat.name,
    value: cat.percentage,
    color: cat.color,
  }));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    const numValue = parseFloat(raw) / 100 || 0;
    setInputValue(formatCurrency(numValue));
    // Update budget reactively as user types for immediate calculation feedback
    setTotalBudget(numValue);
  };

  const handleInputBlur = () => {
    const numValue = parseCurrency(inputValue);
    setTotalBudget(numValue);
    setIsEditing(false);

    // Sync with server if authenticated
    if (isAuthenticated && numValue !== totalBudget) {
      updatePreferences.mutate({
        budgetConfig: {
          totalBudget: numValue,
        },
      });
    }
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  useEffect(() => {
    if (!isEditing) {
      setInputValue(formatCurrency(totalBudget));
    }
  }, [totalBudget, isEditing]);

  if (prefsLoading && isAuthenticated) {
    return (
      <div className="px-4 py-6 flex items-center justify-center min-h-[60vh]">
        <div className={`animate-pulse ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {t('common.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Budget Input Section */}
      <div className="text-center space-y-2">
        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          {t('pages.home.monthlyBudget')}
        </p>
        <div className="relative inline-block">
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            data-testid="budget-input"
            autoFocus
            className="text-4xl md:text-5xl font-bold text-primary-500 bg-transparent border-none outline-none text-center w-full max-w-xs"
          />
        </div>
      </div>

      {/* Donut Chart */}
      <div className="relative">
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
              stroke="transparent"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Center percentage labels */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className={`text-2xl font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>100%</p>
            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total</p>
          </div>
        </div>

        {/* Floating percentage labels around the chart - positioned for each category */}
        {categories[0] && (
          <div className={`absolute top-4 right-8 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[0].percentage}%</div>
        )}
        {categories[1] && (
          <div className={`absolute top-12 right-2 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[1].percentage}%</div>
        )}
        {categories[2] && (
          <div className={`absolute bottom-12 right-4 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[2].percentage}%</div>
        )}
        {categories[3] && (
          <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[3].percentage}%</div>
        )}
        {categories[4] && (
          <div className={`absolute bottom-12 left-4 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[4].percentage}%</div>
        )}
        {categories[5] && (
          <div className={`absolute top-12 left-2 text-xs font-medium ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{categories[5].percentage}%</div>
        )}
      </div>

      {/* Category Breakdown List */}
      <div className="space-y-1" data-testid="category-list">
        {categories.map((category) => {
          const amount = (totalBudget * category.percentage) / 100;

          return (
            <div
              key={category.id}
              data-testid={`category-${category.id}`}
              className={`flex items-center justify-between py-3 px-2 rounded-lg transition-colors ${
                isDark ? 'hover:bg-slate-800/30' : 'hover:bg-slate-100'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <span className={`font-medium ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {getCategoryName(t, category.id, category.name)}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className={`text-sm w-12 text-right ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {category.percentage}%
                </span>
                <span
                  className="text-primary-500 font-semibold font-mono w-24 text-right"
                  data-testid={`amount-${category.id}`}
                >
                  {formatCurrency(amount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Row */}
      <div className={`flex items-center justify-between py-3 px-2 border-t ${
        isDark ? 'border-slate-800' : 'border-slate-200'
      }`}>
        <span className={`font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>Total</span>
        <div className="flex items-center gap-4">
          <span className={`text-sm w-12 text-right ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>100%</span>
          <span className="text-primary-400 font-bold font-mono w-24 text-right" data-testid="total-amount">
            {formatCurrency(totalBudget)}
          </span>
        </div>
      </div>
    </div>
  );
}
